import { Injectable } from '@angular/core'
import { Observable, BehaviorSubject, of } from 'rxjs'
import { map, catchError, tap } from 'rxjs/operators'
import { ApiService } from './api.service'
import { ApplicationType, AwsRegionDto, RegionsResponseDto } from './dtos'

const emptyRegions = {
  aws: [],
  azure: [],
} as RegionsResponseDto

@Injectable({
  providedIn: 'root',
})
export class RegionsService {
  private sortAzure(regions: { label: string; value: string }[]): { label: string; value: string }[] {
    function getOrder(label: string): number {
      /* Put America first! */
      return label.startsWith('(US)') ? 1 : 2
    }

    return regions.sort((a, b) => {
      const orderA = getOrder(a.label)
      const orderB = getOrder(b.label)

      if (orderA != orderB) {
        return orderA - orderB
      }

      return a.label.localeCompare(b.label)
    })
  }

  private sortAws(regions: { label: string; value: string }[]): { label: string; value: string }[] {
    const order = {
      // US regions in specific order
      'us-east-1': 1,
      'us-east-2': 2,
      'us-west-1': 3,
      'us-west-2': 4,

      // Asia Pacific - will be sorted alphabetically within this group
      'ap-': 100,

      // Canada
      'ca-': 200,

      // Europe - will be sorted alphabetically within this group
      'eu-': 300,

      // Middle East
      'me-': 400,

      // Africa
      'af-': 500,

      // South America
      'sa-': 600,

      // Israel
      'il-': 700,
    }

    return regions.sort((a, b) => {
      const getOrder = (region: string): number => {
        // Check for exact matches first (US regions)
        if (order[region]) return order[region]

        // Check for prefix matches
        for (const [prefix, value] of Object.entries(order)) {
          if (region.startsWith(prefix)) return value
        }

        // Default to end if no match
        return 1000
      }

      const orderA = getOrder(a.value)
      const orderB = getOrder(b.value)

      if (orderA !== orderB) {
        return orderA - orderB
      }

      // Within the same group, sort alphabetically
      return a.value.localeCompare(b.value)
    })
  }

  private getRegions(): Observable<RegionsResponseDto> {
    /* Returns an observable representing the server's RegionsResponseDto object.
       We only need to fetch this once to serve the remaining calls in this module.
    */
    if (this.regionsLoaded) {
      return this.regionsSubject.asObservable()
    }

    return this.apiService.getRegions().pipe(
      tap((regions) => {
        this.regionsSubject.next(regions)
        this.regionsLoaded = true
      }),
      catchError((error) => {
        console.error('Failed to load regions from API', error)
        return of(emptyRegions)
      })
    )
  }

  private regionsSubject = new BehaviorSubject<RegionsResponseDto>(emptyRegions)
  private regionsLoaded = false

  constructor(private apiService: ApiService) {}

  /* Returns a dict of displayable label and value */
  getRegionsFormatted(applicationType: ApplicationType): Observable<{ label: string; value: string }[]> {
    if (applicationType == ApplicationType.AZURE) {
      return this.getRegions().pipe(
        map((response) =>
          this.sortAzure(
            response.azure.map((region) => ({
              label: `${region.regionName} (${region.region})`,
              value: region.region,
            }))
          )
        )
      )
    } else {
      return this.getRegions().pipe(
        map((response) =>
          this.sortAws(
            response.aws.map((region) => ({
              label: `${region.regionName} (${region.region})`,
              value: region.region,
            }))
          )
        )
      )
    }
  }

  /* Look up display name from region name.  Returns undefined if region
     not known, or download incomplete. 
  */
  getRegionName(applicationType: ApplicationType, region: string): string {
    if (this.regionsLoaded) {
      if (applicationType == ApplicationType.AZURE) {
        const entry = this.regionsSubject.value.azure.find((r) => r.region == region)
        if (entry) {
          return entry.regionName
        }
      } else {
        const entry = this.regionsSubject.value.aws.find((r) => r.region == region)
        if (entry) {
          return entry.regionName
        }
      }
    }

    return undefined
  }

  isValidRegion(applicationType: ApplicationType, region: string): boolean {
    return this.getRegionName(applicationType, region) !== undefined
  }

  areRegionsLoaded(): boolean {
    return this.regionsLoaded
  }

  azureRegionSupportsAzs(region: string): boolean {
    if (!this.regionsLoaded) {
      return false
    }
    const entry = this.regionsSubject.value.azure.find((r) => r.region === region)
    return entry?.supportsAvailabilityZones ?? false
  }
}
